import CalendarEvent from "../../../classes/CalendarEvent";
import Sale from "../../../classes/Sale";
import SaleItem from "../../../classes/SaleItem";
import SaleLabor from "../../../classes/SaleLabor";
import { db } from "../../../boot/firebase";
import {
  collection,
  deleteField,
  doc,
  runTransaction,
  updateDoc,
} from "firebase/firestore";
import { call } from "../../../util/misc";
import { setLoadedCount } from "../../../util/loadedItems";
import { uniq, without } from "lodash";

export const GET_EVENT = ({ dispatch }, id) => {
  return dispatch("GET_DOC", {
    converter: CalendarEvent,
    path: CalendarEvent.getPath(id),
    stateKey: "events",
    id,
  });
};

export const GET_EVENTS = (
  { dispatch },
  { conditions, limitCount, pagination }
) => {
  return dispatch("GET_COLLECTION", {
    converter: CalendarEvent,
    stateKey: "events",
    path: CalendarEvent.getPath(),
    conditions,
    limitCount,
    pagination,
  });
};

export const LISTEN_EVENT = ({ dispatch }, { id, next, error, unlisten }) => {
  return dispatch("LISTEN_DOC", {
    converter: CalendarEvent,
    path: CalendarEvent.getPath(id),
    stateKey: "events",
    id,
    next,
    error,
    unlisten,
  });
};

export const LISTEN_EVENTS = (
  { dispatch },
  {
    next,
    conditions,
    limitCount,
    pagination,
    listenerPath,
    error,
    unlisten,
    retain,
  }
) => {
  return dispatch("LISTEN_COLLECTION", {
    converter: CalendarEvent,
    stateKey: "events",
    path: CalendarEvent.getPath(),
    listenerPath,
    conditions,
    limitCount,
    pagination,
    next,
    error,
    unlisten,
    retain,
  });
};

// A sale's events: the ones filed under it (`item`, pencils included), and
// the combined deliveries it only rides on. A combined delivery is stored
// under its PRIMARY sale, with every other sale on the truck under
// `additionalSales`. Those sales still carry the event in
// `status.delivery.events`, so without the second listener the ID is in the
// registry but the event is never in the store: `SaleStatus.deliveryEvents`
// compacts it away and SaleEvents shows a permanent skeleton (sale
// 05400045B). Two queries rather than one because Firestore cannot OR across
// fields, the same split as `fetchSaleEvents` (functions/sales-helpers.js)
// and `findEventsWithLoadedItem` (src/util/loadedItems.js).
//
// The sale page and the project card can both hold these, so each caller
// takes its own reference (`retain`) and the events stay until the last one
// leaves. Resolves to both handles, for UNLISTEN.
export const LISTEN_SALE_EVENTS = (
  { dispatch },
  { projectID, saleID, error }
) => {
  const stateID = projectID + saleID;
  return Promise.all([
    dispatch("LISTEN_EVENTS", {
      conditions: ["item", "==", stateID],
      listenerPath: "events-sale-" + stateID,
      error,
      retain: true,
    }),
    dispatch("LISTEN_EVENTS", {
      conditions: [`additionalSales.${stateID}.quantity`, ">", 0],
      listenerPath: "events-sale-additional-" + stateID,
      error,
      retain: true,
    }),
  ]);
};

function getUpdatedScheduledQuantity(item, oldQty, newQty) {
  const currentScheduled = Number(
    item?.quantityScheduled ?? item?.data?.quantityScheduled ?? 0
  );
  const quantity = Number(item?.quantity ?? item?.data?.quantity ?? 0);
  const nextScheduled = Math.max(
    0,
    Math.min(quantity, currentScheduled - oldQty + newQty)
  );
  return {
    currentScheduled,
    nextScheduled,
    quantity,
  };
}

function getScheduledCounterDelta(item, delta) {
  // Child accessories (any item with a `parentID`) feed the dedicated
  // `accessories` counters rather than `units` — they are separate stock but
  // are excluded from the quote-line based `total.items`. Mirrors
  // `classifyItemBucket` (`src/store/main/actions/sales.js`) and
  // `classifySaleItem` (`functions/sales-helpers.js`).
  if (item?.data?.parentID) {
    return { units: 0, screens: 0, hardware: 0, accessories: delta };
  }
  const isScreens = Boolean(item?.isScreens ?? item?.data?.isScreens);
  const isHardware = Boolean(item?.isHardware ?? item?.data?.isHardware);
  const hasLine = Boolean(item?.data?.line);

  if ((isScreens || isHardware) && !hasLine) {
    return {
      units: 0,
      screens: isScreens ? delta : 0,
      hardware: isHardware ? delta : 0,
      accessories: 0,
    };
  }

  return {
    units: delta,
    screens: 0,
    hardware: 0,
    accessories: 0,
  };
}

async function updateSale(state, oldEvent, newEvent, dispatch, tx) {
  const data = newEvent ? newEvent.data : oldEvent.data;
  if (!data || !data.item) {
    return;
  }

  // Pencil events are tentative — they don't participate in sale scheduling.
  // Pure pencil saves (create/edit/delete of a pencil event) are skipped,
  // but a save that transitions INTO or OUT OF pencil must unschedule or
  // schedule the event — otherwise a pencil converted to a real event never
  // lands in the sale's `events` registry (sale 04700086A).
  const wasScheduled = Boolean(oldEvent && !oldEvent.isPencil);
  const isScheduled = Boolean(newEvent && !newEvent.isPencil);
  if (!wasScheduled && !isScheduled) {
    return;
  }

  let changes = null;
  if (data.type === "delivery" || data.type === "installation") {
    // Get Sale
    const projectID = data.item.slice(0, -1);
    const saleID = data.item.slice(-1);
    const saleRef = doc(db, Sale.getPath(projectID, saleID)).withConverter(
      Sale
    );
    const sale = (await tx.get(saleRef)).data();
    const modified = new Date();
    const modifiedBy = state.user.id;

    let events = [...sale.data.status.delivery.events];
    if (isScheduled && !wasScheduled) {
      events.push(newEvent.id);
      events = uniq(events);
    } else if (wasScheduled && !isScheduled) {
      events = without(events, oldEvent.id);
    }

    if (data.type === "delivery") {
      const oldUnitsScheduled = wasScheduled ? oldEvent.unitCount : 0;

      const newUnitsScheduled = isScheduled ? newEvent.unitCount : 0;

      const oldScreensScheduled = wasScheduled ? oldEvent.screenCount : 0;

      const newScreensScheduled = isScheduled ? newEvent.screenCount : 0;

      const oldHardwareScheduled = wasScheduled ? oldEvent.hardwareCount : 0;

      const newHardwareScheduled = isScheduled ? newEvent.hardwareCount : 0;

      const oldAccessoriesScheduled = wasScheduled
        ? oldEvent.accessoryCount
        : 0;

      const newAccessoriesScheduled = isScheduled ? newEvent.accessoryCount : 0;

      changes = {
        "status.delivery.events": events,
        "status.delivery.scheduled":
          sale.data.status.delivery.scheduled -
          oldUnitsScheduled +
          newUnitsScheduled,
        "status.delivery.screensScheduled":
          sale.data.status.delivery.screensScheduled -
          oldScreensScheduled +
          newScreensScheduled,
        "status.delivery.hardwareScheduled":
          sale.data.status.delivery.hardwareScheduled -
          oldHardwareScheduled +
          newHardwareScheduled,
        "status.delivery.accessoriesScheduled": Math.max(
          0,
          (sale.data.status.delivery.accessoriesScheduled || 0) -
            oldAccessoriesScheduled +
            newAccessoriesScheduled
        ),
      };

      // Update Sale Items
      let oldAndNewItems = uniq(
        (isScheduled ? Object.keys(newEvent.data.items || {}) : []).concat(
          wasScheduled ? Object.keys(oldEvent.data.items || {}) : []
        )
      );
      let itemRefs = {};
      let items = {};
      await Promise.all(
        oldAndNewItems.map(async (id) => {
          let ref = doc(
            db,
            SaleItem.getPath(projectID, saleID, id)
          ).withConverter(SaleItem);
          itemRefs[id] = ref;
          items[id] = (await tx.get(ref)).data();
        })
      );

      // Begin tx writes
      oldAndNewItems.map((id) => {
        let ref = itemRefs[id];
        let item = items[id];
        if (!item) {
          return;
        }
        let newQty = isScheduled ? (newEvent.data.items || {})[id] || 0 : 0;

        let oldQty = wasScheduled ? (oldEvent.data.items || {})[id] || 0 : 0;
        if (newQty !== oldQty) {
          const { currentScheduled, nextScheduled, quantity } =
            getUpdatedScheduledQuantity(item, oldQty, newQty);
          if (nextScheduled !== currentScheduled) {
            tx.update(ref, {
              modified,
              modifiedBy,
              quantityScheduled: nextScheduled,
              fullyScheduled: nextScheduled === quantity,
            });
          }
        }
      });
    } else if (data.type === "installation") {
      let events = [...sale.data.status.install.events];
      if (isScheduled && !wasScheduled) {
        events.push(newEvent.id);
        events = uniq(events);
      } else if (wasScheduled && !isScheduled) {
        events = without(events, oldEvent.id);
      }

      // Update Sale Labors
      const newItems = isScheduled
        ? (newEvent.data.items || []).concat().sort()
        : [];
      const oldItems = wasScheduled
        ? (oldEvent.data.items || []).concat().sort()
        : [];
      const oldAndNewItems = uniq(newItems.concat(oldItems));
      const itemRefs = {};
      const items = {};
      await Promise.all(
        oldAndNewItems.map(async (id) => {
          const ref = doc(
            db,
            SaleLabor.getPath(projectID, saleID, id)
          ).withConverter(SaleLabor);
          itemRefs[id] = ref;
          items[id] = (await tx.get(ref)).data();
        })
      );

      // Begin tx writes
      let newHoursToDistribute = isScheduled ? newEvent.manHours || 0 : 0;
      let oldHoursToDistribute = wasScheduled ? oldEvent.manHours || 0 : 0;
      oldAndNewItems.map((id) => {
        const ref = itemRefs[id];
        const item = items[id];
        const isLastNew = newItems.indexOf(id) === newItems.length - 1;
        const isLastOld = oldItems.indexOf(id) === oldItems.length - 1;
        let newHrs = 0;
        if (isScheduled) {
          if (!isLastNew && newHoursToDistribute >= item.data.manHours) {
            newHrs = item.data.manHours;
          } else {
            newHrs = newHoursToDistribute;
          }
          newHoursToDistribute -= newHrs;
        }
        let oldHrs = 0;
        if (wasScheduled) {
          if (!isLastOld && oldHoursToDistribute >= item.data.manHours) {
            oldHrs = item.data.manHours;
          } else {
            oldHrs = oldHoursToDistribute;
          }
          oldHoursToDistribute -= oldHrs;
        }
        if (newHrs !== oldHrs) {
          const hoursScheduled =
            item.data.status.hoursScheduled - oldHrs + newHrs;
          tx.update(ref, {
            modified,
            modifiedBy,
            "status.hoursScheduled": hoursScheduled,
            ["status.events." + (newEvent || oldEvent).id]: isScheduled
              ? newHrs || deleteField()
              : deleteField(),
            "status.fullyScheduled": hoursScheduled >= item.data.manHours,
          });
        }
      });

      changes = {
        "status.install.events": events,
        "status.install.scheduledHours":
          sale.data.status.install.scheduledHours -
          (wasScheduled ? oldEvent.manHours : 0) +
          (isScheduled ? newEvent.manHours : 0),
      };
    }

    // Update Sale for delivery/installation
    if (changes !== null) {
      return dispatch("UPDATE_SALE_STATUS", {
        batch: tx,
        projectID,
        saleID,
        changes,
      });
    }
  } else if (data.type === "pickup") {
    // Get Sale
    const projectID = data.item.slice(0, -1);
    const saleID = data.item.slice(-1);
    const saleRef = doc(db, Sale.getPath(projectID, saleID)).withConverter(
      Sale
    );
    const sale = (await tx.get(saleRef)).data();
    const modified = new Date();
    const modifiedBy = state.user.id;

    let events = [...(sale.data.status.pickup?.events || [])];
    if (isScheduled && !wasScheduled) {
      events.push(newEvent.id);
      events = uniq(events);
    } else if (wasScheduled && !isScheduled) {
      events = without(events, oldEvent.id);
    }

    const oldUnitsScheduled = wasScheduled ? oldEvent.unitCount : 0;
    const newUnitsScheduled = isScheduled ? newEvent.unitCount : 0;
    const oldScreensScheduled = wasScheduled ? oldEvent.screenCount : 0;
    const newScreensScheduled = isScheduled ? newEvent.screenCount : 0;
    const oldHardwareScheduled = wasScheduled ? oldEvent.hardwareCount : 0;
    const newHardwareScheduled = isScheduled ? newEvent.hardwareCount : 0;
    const oldAccessoriesScheduled = wasScheduled ? oldEvent.accessoryCount : 0;
    const newAccessoriesScheduled = isScheduled ? newEvent.accessoryCount : 0;

    // The registry is always `status.pickup.events` (repairSale rebuilds it
    // from every pickup-type event), but the scheduled COUNTERS belong to
    // the bucket the sale is tracked in: an ad-hoc pickup against a jobsite
    // sale feeds `status.delivery.*`, the same bucket `completePickup` adds
    // its completions to and repairSale section 3b rebuilds. The #888 merge
    // dropped this routing, so jobsite pickups bumped `status.pickup.*`
    // instead and never counted towards the sale's delivery gates.
    const bucket = sale.data.shipTo?.pickup ? "pickup" : "delivery";
    const counters = sale.data.status[bucket] || {};
    changes = {
      "status.pickup.events": events,
      [`status.${bucket}.scheduled`]:
        (counters.scheduled || 0) - oldUnitsScheduled + newUnitsScheduled,
      [`status.${bucket}.screensScheduled`]:
        (counters.screensScheduled || 0) -
        oldScreensScheduled +
        newScreensScheduled,
      [`status.${bucket}.hardwareScheduled`]:
        (counters.hardwareScheduled || 0) -
        oldHardwareScheduled +
        newHardwareScheduled,
      [`status.${bucket}.accessoriesScheduled`]: Math.max(
        0,
        (counters.accessoriesScheduled || 0) -
          oldAccessoriesScheduled +
          newAccessoriesScheduled
      ),
    };

    // Update Sale Items
    let oldAndNewItems = uniq(
      (isScheduled ? Object.keys(newEvent.data.items || {}) : []).concat(
        wasScheduled ? Object.keys(oldEvent.data.items || {}) : []
      )
    );
    let itemRefs = {};
    let items = {};
    await Promise.all(
      oldAndNewItems.map(async (id) => {
        let ref = doc(
          db,
          SaleItem.getPath(projectID, saleID, id)
        ).withConverter(SaleItem);
        itemRefs[id] = ref;
        items[id] = (await tx.get(ref)).data();
      })
    );

    // Begin tx writes - update quantityScheduled for pickup
    oldAndNewItems.map((id) => {
      let ref = itemRefs[id];
      let item = items[id];
      if (!item) {
        return;
      }
      let newQty = isScheduled ? (newEvent.data.items || {})[id] || 0 : 0;
      let oldQty = wasScheduled ? (oldEvent.data.items || {})[id] || 0 : 0;
      if (newQty !== oldQty) {
        // Item-level: clamp quantityScheduled to [0, quantity]. Rescheduling
        // a partial pickup's remainder on a new event otherwise pushed it
        // past the item's quantity (sale 50100032A: 4 of 2).
        const { currentScheduled, nextScheduled, quantity } =
          getUpdatedScheduledQuantity(item, oldQty, newQty);
        if (nextScheduled !== currentScheduled) {
          tx.update(ref, {
            modified,
            modifiedBy,
            quantityScheduled: nextScheduled,
            fullyScheduled: nextScheduled === quantity,
          });
        }
      }
    });

    // Update Sale for pickup
    return dispatch("UPDATE_SALE_STATUS", {
      batch: tx,
      projectID,
      saleID,
      changes,
    });
  }
}

async function readAdditionalSales(state, oldEvent, newEvent, dispatch, tx) {
  const data = newEvent ? newEvent.data : oldEvent.data;
  if (!data || data.type !== "delivery") {
    return null;
  }

  const newAdditional = (newEvent && newEvent.data.additionalSales) || {};
  const oldAdditional = (oldEvent && oldEvent.data.additionalSales) || {};
  const allSaleStateIDs = uniq(
    Object.keys(newAdditional).concat(Object.keys(oldAdditional))
  );

  if (!allSaleStateIDs.length) {
    return null;
  }

  const eventID = (newEvent || oldEvent).id;

  // Perform all transactional reads upfront
  const saleReads = {};
  for (const saleStateID of allSaleStateIDs) {
    const projectID = saleStateID.slice(0, -1);
    const saleID = saleStateID.slice(-1);

    const oldSaleData = oldAdditional[saleStateID];
    const newSaleData = newAdditional[saleStateID];
    const oldItems = oldSaleData ? oldSaleData.items || {} : {};
    const newItems = newSaleData ? newSaleData.items || {} : {};
    const allItemIDs = uniq(
      Object.keys(newItems).concat(Object.keys(oldItems))
    );

    const saleRef = doc(db, Sale.getPath(projectID, saleID)).withConverter(
      Sale
    );
    const sale = (await tx.get(saleRef)).data();

    const itemRefs = {};
    const items = {};
    await Promise.all(
      allItemIDs.map(async (id) => {
        const ref = doc(
          db,
          SaleItem.getPath(projectID, saleID, id)
        ).withConverter(SaleItem);
        itemRefs[id] = ref;
        items[id] = (await tx.get(ref)).data();
      })
    );

    // Pre-cache the sale for UPDATE_SALE_STATUS
    await dispatch("GET_SALE", { projectID, saleID });

    saleReads[saleStateID] = {
      projectID,
      saleID,
      sale,
      oldSaleData,
      newSaleData,
      oldItems,
      newItems,
      allItemIDs,
      itemRefs,
      items,
    };
  }

  return { saleReads, allSaleStateIDs, eventID };
}

async function writeAdditionalSales(state, readResult, dispatch, tx) {
  if (!readResult) {
    return;
  }

  const { saleReads, allSaleStateIDs, eventID } = readResult;
  const modified = new Date();
  const modifiedBy = state.user.id;

  for (const saleStateID of allSaleStateIDs) {
    const {
      projectID,
      saleID,
      sale,
      oldSaleData,
      newSaleData,
      oldItems,
      newItems,
      allItemIDs,
      itemRefs,
      items,
    } = saleReads[saleStateID];

    let events = [...(sale.data.status.delivery?.events || [])];
    if (newSaleData && !oldSaleData) {
      events.push(eventID);
      events = uniq(events);
    } else if (oldSaleData && !newSaleData) {
      events = without(events, eventID);
    }

    let oldUnits = 0,
      oldScreens = 0,
      oldHardware = 0,
      oldAccessories = 0;
    let newUnits = 0,
      newScreens = 0,
      newHardware = 0,
      newAccessories = 0;
    allItemIDs.forEach((id) => {
      const item = items[id];
      // Child accessories are counted separately — mirrors
      // `classifyItemBucket` and `CalendarEvent.accessoryCount`.
      const isChildAccessory = Boolean(item && item.data.parentID);
      const isUnNumberedScreen =
        item && !isChildAccessory && item.isScreens && !item.data.line;
      const isUnNumberedHardware =
        item && !isChildAccessory && item.isHardware && !item.data.line;
      const oldQty = oldItems[id] || 0;
      const newQty = newItems[id] || 0;

      if (isChildAccessory) {
        oldAccessories += oldQty;
        newAccessories += newQty;
      } else if (isUnNumberedScreen) {
        oldScreens += oldQty;
        newScreens += newQty;
      } else if (isUnNumberedHardware) {
        oldHardware += oldQty;
        newHardware += newQty;
      } else {
        oldUnits += oldQty;
        newUnits += newQty;
      }

      if (item && newQty !== oldQty) {
        const { currentScheduled, nextScheduled, quantity } =
          getUpdatedScheduledQuantity(item, oldQty, newQty);
        if (nextScheduled !== currentScheduled) {
          tx.update(itemRefs[id], {
            modified,
            modifiedBy,
            quantityScheduled: nextScheduled,
            fullyScheduled: nextScheduled === quantity,
          });
        }
      }
    });

    const deliveryStatus = sale.data.status.delivery || {};
    const changes = {
      "status.delivery.events": events,
      "status.delivery.scheduled":
        (deliveryStatus.scheduled || 0) - oldUnits + newUnits,
      "status.delivery.screensScheduled":
        (deliveryStatus.screensScheduled || 0) - oldScreens + newScreens,
      "status.delivery.hardwareScheduled":
        (deliveryStatus.hardwareScheduled || 0) - oldHardware + newHardware,
      "status.delivery.accessoriesScheduled": Math.max(
        0,
        (deliveryStatus.accessoriesScheduled || 0) -
          oldAccessories +
          newAccessories
      ),
    };

    await dispatch("UPDATE_SALE_STATUS", {
      batch: tx,
      projectID,
      saleID,
      changes,
    });
  }
}

// Server-owned fields on a delivery event's `status`. Every one of these is
// written only by the delivery cloud functions (beginLoadingTruck /
// endLoadingTruck / unloadTruck / completeDelivery / the invoice functions),
// never by the event edit dialog.
//
// The complement — the fields EventEdit.vue legitimately owns — is `type`,
// `isTemporary`, `shouldInvoiceAnyway`, `notifyCustomer`, `departed` and
// `arrived`. `departed`/`arrived`/`type` are deliberately absent below because
// the reschedule path in EventEdit.vue writes them and must win.
//
// Installation events carry only `isTemporary`/`shouldInvoiceAnyway` in status
// (INSTALLATION_STATUS_FIELDS), so none of these apply and preserving is a
// no-op for them.
export const PRESERVED_LOADING_FIELDS = [
  "loadingStarted",
  "loadingCompleted",
  "deliveryVehicle",
  "loaded",
  "delivered",
  "completedStops",
  "unloaded",
  "undeliveredUnloaded",
  "signature",
  "signerName",
  "noSignature",
  "invoice",
  // Combined deliveries: the additional sales' invoice numbers, written by the
  // same touchSale trigger that writes `invoice`.
  "additionalInvoices",
  // Invoice numbers handed out per sale by getInvoiceID (server-owned).
  "invoiceClaims",
  "proof",
  "pdf",
];

// Re-apply the authoritative server values of the server-owned status fields
// onto a buffered status that is about to overwrite the event document.
// Mutates and returns `bufferStatus`.
export function preserveServerLoadingFields(serverStatus, bufferStatus) {
  if (!serverStatus || !bufferStatus) {
    return bufferStatus;
  }
  for (const field of PRESERVED_LOADING_FIELDS) {
    if (field in serverStatus) {
      bufferStatus[field] = serverStatus[field];
    }
  }
  return bufferStatus;
}

export const SAVE_EVENT = async ({ dispatch, state }, event) => {
  return runTransaction(db, async (tx) => {
    const saveDocuments = (existingData) => {
      return dispatch("SAVE_DOCUMENTS", {
        batch: tx,
        parentData: event.data,
        fieldPath: "attachments",
        folderPath: event.path,
        existingData,
      });
    };

    if (!event.data.created) {
      // New CalendarEvent
      event.data.created = new Date();
      event.data.createdBy = state.user.id;

      const ref = doc(collection(db, CalendarEvent.getPath()));
      event.id = ref.id;

      // All reads before any writes (Firestore transaction requirement)
      const additionalReads = await readAdditionalSales(
        state,
        null,
        event,
        dispatch,
        tx
      );
      await updateSale(state, null, event, dispatch, tx);
      await writeAdditionalSales(state, additionalReads, dispatch, tx);
      await saveDocuments();
      tx.set(ref.withConverter(CalendarEvent), event);
    } else {
      // Existing CalendarEvent
      event.data.modified = new Date();
      event.data.modifiedBy = state.user.id;
      const current = await dispatch("GET_EVENT", event.id);

      // All reads before any writes (Firestore transaction requirement)
      const additionalReads = await readAdditionalSales(
        state,
        current,
        event,
        dispatch,
        tx
      );
      // tx.set below overwrites the whole event document, so a status map
      // snapshotted when the edit dialog opened would clobber any loading or
      // delivery progress the cloud functions recorded in the meantime. Re-apply
      // the server-owned fields onto the buffer before writing (sale 03900058B).
      if (current?.data?.status && event.data?.status) {
        preserveServerLoadingFields(current.data.status, event.data.status);
      }
      await updateSale(state, current, event, dispatch, tx);
      await writeAdditionalSales(state, additionalReads, dispatch, tx);
      await saveDocuments(current.data);
      tx.set(doc(db, event.path).withConverter(CalendarEvent), event);
    }

    return event;
  });
};

export const UPDATE_EVENT = ({ state }, { id, changes }) => {
  changes.modified = new Date();
  changes.modifiedBy = state.user.id;
  return updateDoc(doc(db, CalendarEvent.getPath(id)), changes);
};

export const UPDATE_EVENT_LOADED = ({ state }, { id, key, count }) =>
  setLoadedCount(doc(db, CalendarEvent.getPath(id)), key, count, state.user.id);

export const DELETE_EVENT = ({ commit, dispatch, state }, id) => {
  return runTransaction(db, async (tx) => {
    const ref = doc(db, CalendarEvent.getPath(id)).withConverter(CalendarEvent);
    const event = (await tx.get(ref)).data();
    // All reads before any writes (Firestore transaction requirement)
    const additionalReads = await readAdditionalSales(
      state,
      event,
      null,
      dispatch,
      tx
    );
    await updateSale(state, event, null, dispatch, tx);
    await writeAdditionalSales(state, additionalReads, dispatch, tx);
    tx.delete(ref);
    commit("REMOVE", { stateKey: "events", id });
  });
};

export const RESCHEDULE_EVENT = (
  context,
  { eventID, reason, includeInstallations }
) => {
  return call("rescheduleEvent", { eventID, reason, includeInstallations });
};
